import * as React from 'react'
import { useShallow } from 'zustand/shallow'
import {
  NewsItem,
  publishedTime,
  useQueryNews,
} from '@/modules/shared/query-client/useQueryNews'
import { useNewsSeenStore } from '@/modules/shared/stores/newsSeenStore'

const FIRST_VISIT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000

const NO_ITEMS: NewsItem[] = []

export type NewsFeed = {
  items: NewsItem[]
  unseen: NewsItem[]
  unseenCount: number
  isLoading: boolean
  shouldAnnounce: boolean
  markAllSeen: () => void
  isUnseen: (item: NewsItem) => boolean
  lastSeenAt: number | null
  announcementsMuted: boolean
  setAnnouncementsMuted: (muted: boolean) => void
}

export function useNewsFeed(): NewsFeed {
  const { data, isLoading } = useQueryNews()
  const {
    seenIds,
    lastSeenAt,
    initialized,
    announcementsMuted,
    markSeen,
    initialize,
    setAnnouncementsMuted,
  } = useNewsSeenStore(
    useShallow((s) => ({
      seenIds: s.seenIds,
      lastSeenAt: s.lastSeenAt,
      initialized: s.initialized,
      announcementsMuted: s.announcementsMuted,
      markSeen: s.markSeen,
      initialize: s.initialize,
      setAnnouncementsMuted: s.setAnnouncementsMuted,
    })),
  )

  const items = data ?? NO_ITEMS

  React.useEffect(() => {
    if (initialized || isLoading || items.length === 0) return
    const cutoff = Date.now() - FIRST_VISIT_WINDOW_MS
    initialize(
      items.filter((item) => publishedTime(item) < cutoff).map((item) => item.id),
    )
  }, [initialized, isLoading, items, initialize])

  const seen = React.useMemo(() => new Set(seenIds), [seenIds])

  const unseen = React.useMemo(() => {
    if (!initialized) return NO_ITEMS
    const fresh = items.filter((item) => !seen.has(item.id))
    return fresh.length > 0 ? fresh : NO_ITEMS
  }, [initialized, items, seen])

  const markAllSeen = React.useCallback(() => {
    markSeen(items.map((item) => item.id))
  }, [items, markSeen])

  const isUnseen = React.useCallback(
    (item: NewsItem) => !seen.has(item.id),
    [seen],
  )

  return {
    items,
    unseen,
    unseenCount: unseen.length,
    isLoading,
    shouldAnnounce: initialized && unseen.length > 0 && !announcementsMuted,
    markAllSeen,
    isUnseen,
    lastSeenAt,
    announcementsMuted,
    setAnnouncementsMuted,
  }
}
